import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { clearUserLogged, logoutAction, setSessionExpired, switchIdpAccount } from 'features/common/commonSlice';
import {
  getGroupAiNotesConfigAsync,
  getGroupAiNotesUsageAsync,
  getGroupAiNotesCallsAsync,
  getExpedientAiNotesAsync,
  getAiNoteLiveAsync,
  acceptAiNoteAsync,
  discardAiNoteAsync,
  resolveOutcomeChangesNote,
} from './asyncActions';
import { isStaleAiNote } from './aiNotesUtils';
import { AiCallNote, AiNotesState } from './types';

/**
 * Notas de llamada con IA: config y consumo por grupo (pestaña «Notas IA»), notas IA por expediente
 * y la nota de la llamada que sigue la atención (sondeo). La nota viva y las listas se cruzan por
 * eventId para que aceptar/descartar en un sitio se vea en el otro.
 */
export const initialState: AiNotesState = {
  groupConfig: { groupId: null, item: null, status: 'idle' },
  usage: { groupId: null, item: null, status: 'idle' },
  calls: { groupId: null, month: null, page: 0, size: 50, results: [], status: 'idle' },
  byExpedientId: {},
  trackedEventId: null,
  live: { eventId: null, item: null, expired: false },
  resolvingEventIds: [],
  editingDrafts: {},
};

const EMPTY_LIVE: AiNotesState['live'] = { eventId: null, item: null, expired: false };

/** Actualiza la nota en cada lista de expediente que la tenga, sin pisar una foto más nueva. */
const mergeIntoExpedientLists = (state: AiNotesState, note: AiCallNote) => {
  Object.values(state.byExpedientId).forEach((entry) => {
    const index = entry.results.findIndex((item) => item.eventId === note.eventId);
    if (index >= 0 && !isStaleAiNote(entry.results[index], note)) entry.results[index] = note;
  });
};

/** Lleva a la nota viva la versión de la lista si es de la llamada seguida y más nueva. */
const mergeIntoLive = (state: AiNotesState, notes: AiCallNote[]) => {
  const { trackedEventId, live } = state;
  if (!trackedEventId || live.eventId !== trackedEventId || !live.item) return;
  const fromList = notes.find((note) => note.eventId === trackedEventId);
  if (fromList && !isStaleAiNote(live.item, fromList)) live.item = fromList;
};

const sameCallsQuery = (calls: AiNotesState['calls'], arg: any) =>
  calls.groupId === (arg?.groupId ?? null) &&
  calls.month === (arg?.month ?? null) &&
  calls.page === (arg?.page ?? 0) &&
  calls.size === (arg?.size ?? 50);

const addResolving = (state: AiNotesState, eventId: string | undefined) => {
  if (eventId && !state.resolvingEventIds.includes(eventId)) state.resolvingEventIds.push(eventId);
};

const removeResolving = (state: AiNotesState, eventId: string | undefined) => {
  state.resolvingEventIds = state.resolvingEventIds.filter((id) => id !== eventId);
};

export const aiNotesSlice = createSlice({
  name: 'aiNotes',
  initialState,
  reducers: {
    /** El tracker empieza a seguir otra llamada: la nota de la anterior deja de valer. */
    setAiNotesTrackedEvent: (state, action: PayloadAction<string | null>) => {
      if (state.trackedEventId === action.payload) return;
      state.trackedEventId = action.payload;
      state.live = { ...EMPTY_LIVE };
    },
    /** Al montar y desmontar el tracker: sin llamada seguida, cualquier respuesta en vuelo se descarta. */
    clearAiNotesLive: (state) => {
      state.trackedEventId = null;
      state.live = { ...EMPTY_LIVE };
    },
    /** El tracker deja de esperar la nota de esa llamada (solo si sigue siendo la seguida). */
    expireAiNotesLive: (state, action: PayloadAction<string>) => {
      if (state.trackedEventId && state.trackedEventId === action.payload) state.live.expired = true;
    },
    setAiNoteEditText: (state, action: PayloadAction<{ eventId: string; text: string }>) => {
      state.editingDrafts[action.payload.eventId] = action.payload.text;
    },
    clearAiNoteEditText: (state, action: PayloadAction<string>) => {
      delete state.editingDrafts[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getGroupAiNotesConfigAsync.pending, (state) => {
        state.groupConfig.status = 'loading';
      })
      .addCase(getGroupAiNotesConfigAsync.fulfilled, (state, action) => {
        state.groupConfig.status = 'idle';
        state.groupConfig.groupId = action.meta.arg;
        state.groupConfig.item = action.payload ?? null;
      })
      .addCase(getGroupAiNotesConfigAsync.rejected, (state) => {
        state.groupConfig.status = 'failed';
      })
      .addCase(getGroupAiNotesUsageAsync.pending, (state) => {
        state.usage.status = 'loading';
      })
      .addCase(getGroupAiNotesUsageAsync.fulfilled, (state, action) => {
        state.usage.status = 'idle';
        state.usage.groupId = action.meta.arg?.groupId ?? null;
        state.usage.item = action.payload ?? null;
      })
      .addCase(getGroupAiNotesUsageAsync.rejected, (state) => {
        state.usage.status = 'failed';
      })
      .addCase(getGroupAiNotesCallsAsync.pending, (state, action) => {
        // Otra consulta (grupo, mes, página o tamaño): fuera las filas de la anterior mientras carga
        if (!sameCallsQuery(state.calls, action.meta.arg)) state.calls.results = [];
        state.calls.status = 'loading';
        state.calls.groupId = action.meta.arg?.groupId ?? null;
        state.calls.month = action.meta.arg?.month ?? null;
        state.calls.page = action.meta.arg?.page ?? 0;
        state.calls.size = action.meta.arg?.size ?? 50;
      })
      .addCase(getGroupAiNotesCallsAsync.fulfilled, (state, action) => {
        if (!sameCallsQuery(state.calls, action.meta.arg)) return; // respuesta tardía de otro mes/página
        state.calls.status = 'idle';
        state.calls.results = action.payload ?? [];
      })
      .addCase(getGroupAiNotesCallsAsync.rejected, (state, action) => {
        if (!sameCallsQuery(state.calls, action.meta.arg)) return;
        state.calls.status = 'failed';
        state.calls.results = [];
      })
      .addCase(getExpedientAiNotesAsync.pending, (state, action) => {
        const expedientId: string = action.meta.arg;
        const entry = state.byExpedientId[expedientId];
        if (entry) {
          // Mismo expediente: se conserva la lista anterior mientras carga
          entry.status = 'loading';
          entry.requestId = action.meta.requestId;
        } else {
          state.byExpedientId[expedientId] = { results: [], status: 'loading', requestId: action.meta.requestId };
        }
      })
      .addCase(getExpedientAiNotesAsync.fulfilled, (state, action) => {
        const entry = state.byExpedientId[action.meta.arg];
        // Sin entrada (reinicio de sesión) o respuesta de una petición anterior a la última: fuera
        if (!entry || entry.requestId !== action.meta.requestId) return;
        const incoming: AiCallNote[] = action.payload ?? [];
        entry.status = 'idle';
        entry.results = incoming.map((note) => {
          const current = entry.results.find((item) => item.eventId === note.eventId);
          return current && isStaleAiNote(current, note) ? current : note;
        });
        mergeIntoLive(state, incoming);
      })
      .addCase(getExpedientAiNotesAsync.rejected, (state, action) => {
        const entry = state.byExpedientId[action.meta.arg];
        if (!entry || entry.requestId !== action.meta.requestId) return;
        entry.status = 'failed';
      })
      .addCase(getAiNoteLiveAsync.fulfilled, (state, action) => {
        const eventId: string = action.meta.arg;
        // Solo la llamada que sigue el tracker: una respuesta de otra (aceptar en el diálogo, la
        // llamada anterior) o que llega tras el clear no debe pintarse en el bloque de la atención.
        if (!eventId || eventId !== state.trackedEventId) return;
        const incoming: AiCallNote | null = action.payload ?? null;
        const current = state.live.eventId === eventId ? state.live.item : null;
        state.live.eventId = eventId;
        if (!isStaleAiNote(current, incoming)) state.live.item = incoming;
        if (incoming) mergeIntoExpedientLists(state, incoming);
      })
      .addCase(acceptAiNoteAsync.pending, (state, action) => {
        addResolving(state, action.meta.arg?.eventId);
      })
      .addCase(acceptAiNoteAsync.fulfilled, (state, action) => {
        const eventId = action.meta.arg?.eventId;
        removeResolving(state, eventId);
        // Guardada, ya resuelta por otro o inexistente: la edición en curso ya no tiene dónde ir
        if (eventId && resolveOutcomeChangesNote(action.payload?.data)) delete state.editingDrafts[eventId];
      })
      .addCase(acceptAiNoteAsync.rejected, (state, action) => {
        removeResolving(state, action.meta.arg?.eventId);
      })
      .addCase(discardAiNoteAsync.pending, (state, action) => {
        addResolving(state, action.meta.arg?.eventId);
      })
      .addCase(discardAiNoteAsync.fulfilled, (state, action) => {
        const eventId = action.meta.arg?.eventId;
        removeResolving(state, eventId);
        if (eventId && resolveOutcomeChangesNote(action.payload?.data)) delete state.editingDrafts[eventId];
      })
      .addCase(discardAiNoteAsync.rejected, (state, action) => {
        removeResolving(state, action.meta.arg?.eventId);
      })
      // Logout SPA, sesión caducada o cambio de cuenta del IdP: sin esto, el siguiente usuario vería
      // las notas (y las ediciones a medias) del anterior. Igual que los slices filters y releaseNotes.
      .addCase(logoutAction, () => initialState)
      .addCase(clearUserLogged, () => initialState)
      .addCase(setSessionExpired, () => initialState)
      .addCase(switchIdpAccount, () => initialState);
  },
});

export const { setAiNotesTrackedEvent, clearAiNotesLive, expireAiNotesLive, setAiNoteEditText, clearAiNoteEditText } =
  aiNotesSlice.actions;

export default aiNotesSlice.reducer;
